import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { FetchChatTrackerUsersAction } from "../../../Actions/AdminUser";
import ChatTrackerNav from "./ChatTrackerNav";

const ChatTrackerHome = ({
  fetchChatTrackerUsers,
  chatTrackerData,
  loading,
}) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("30");
  const [viewMode, setViewMode] = useState("users");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // Fetch data from API using redux action
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await fetchChatTrackerUsers();
        setIsLoading(false);
      } catch (error) {
        console.error("Error fetching chat tracker data:", error);
        setIsLoading(false);
      }
    };

    loadData();
  }, [fetchChatTrackerUsers]);

  // Process the data from Redux when it changes
  useEffect(() => {
    if (chatTrackerData) {
      // Process the data from the API response
      const results = chatTrackerData.results || {};
      const totalUsers = results.total_users || 0;
      const users = results.users || [];

      // Set stats from the API data
      setStats({
        total: totalUsers,
        active: users.filter((user) => user.session_count > 10).length,
        inactive: users.filter((user) => user.session_count <= 10).length,
      });

      // Transform users into conversation format for display
      const formattedConversations = users.map((user, index) => ({
        id: user.chat_id || `chat-${index}`,
        userId: user.user_id,
        userName: user.username || user.email || "Unknown User",
        summary: `Had ${user.session_count} sessions with ${user.message_count} messages`,
        lastUpdated: new Date(user.last_activity || Date.now()),
        status: user.session_count > 10 ? "active" : "inactive",
        department: "Unknown",
        topic: "Various",
      }));

      setConversations(formattedConversations);
    }
  }, [chatTrackerData]);

  // Handle navigation to conversation detail
  const handleNavigateToConversation = (conversation) => {
    navigate(`/admin-dashboard/chat-tracker/conversation/${conversation.id}`, {
      state: { userId: conversation.userId },
    });
  };

  // Filter conversations based on selected filters
  const filteredConversations = conversations.filter((conv) => {
    // Filter by status
    if (statusFilter !== "all" && conv.status !== statusFilter) {
      return false;
    }

    // Filter by time - using placeholder since we don't have real timestamps
    const daysAgo = parseInt(timeFilter, 10);
    if (daysAgo > 0) {
      // For demo purposes, assume all conversations are within the time filter
      return true;
    }

    return true;
  });

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredConversations.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  const totalPages = Math.ceil(filteredConversations.length / itemsPerPage);

  const formatDate = (date) => {
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) {
      return `${diffMins} min${diffMins !== 1 ? "s" : ""} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
    } else {
      return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
    }
  };

  return (
    <div
      className="relative flex w-full border-l"
      style={{
        background:
          "radial-gradient(ellipse at top left, #dbeafe 0%, transparent 50%), radial-gradient(ellipse at top right, #e0e7ff 0%, transparent 50%), radial-gradient(ellipse at bottom center, #eff6ff 0%, transparent 60%), #f8faff",
      }}
    >
      <div className="w-full flex flex-col">
        <ChatTrackerNav />

        <div className="w-full flex flex-col px-4 md:px-8 pt-6 md:pt-8 pb-16 gap-6">
          <div className="max-w-7xl mx-auto w-full">
            {/* Stats Cards (DashboardHome-style) */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 mb-6">
              <div
                className="w-full rounded-xl px-5 py-5 flex flex-col gap-3 border border-blue-100"
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                }}
              >
                <dt className="font-inter text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Total Conversations
                </dt>
                <dd className="font-inter text-4xl font-semibold text-gray-900">
                  {stats.total}
                </dd>
                <div className="text-xs text-gray-400 font-inter border-t border-blue-100 pt-2">
                  All conversations
                </div>
              </div>

              <div
                className="w-full rounded-xl px-5 py-5 flex flex-col gap-3 border border-green-100"
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                }}
              >
                <dt className="font-inter text-xs font-medium text-green-600 uppercase tracking-wider">
                  Active Conversations
                </dt>
                <dd className="font-inter text-4xl font-semibold text-green-600">
                  {stats.active}
                </dd>
                <div className="text-xs text-gray-400 font-inter border-t border-green-100 pt-2">
                  High engagement
                </div>
              </div>

              <div
                className="w-full rounded-xl px-5 py-5 flex flex-col gap-3 border border-red-100"
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #fff5f5 100%)",
                }}
              >
                <dt className="font-inter text-xs font-medium text-red-500 uppercase tracking-wider">
                  Inactive Conversations
                </dt>
                <dd className="font-inter text-4xl font-semibold text-red-500">
                  {stats.inactive}
                </dd>
                <div className="text-xs text-gray-400 font-inter border-t border-red-100 pt-2">
                  Needs follow-up
                </div>
              </div>
            </div>

            {/* Conversation List (DashboardHome table card style) */}
            <div className="bg-white border border-blue-100 rounded-xl overflow-hidden flex flex-col">
              <div
                className="px-6 py-4 border-b border-blue-100 rounded-t-xl"
                style={{
                  background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)",
                }}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
                    Conversation History
                  </h3>
                  <div className="flex items-center gap-2">
                    <select
                      className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-200/80 bg-white/70 backdrop-blur focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 rounded-lg text-gray-700"
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                    >
                      <option value="all">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>

                    <select
                      className="block w-full pl-3 pr-10 py-2 text-sm border border-gray-200/80 bg-white/70 backdrop-blur focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-400 rounded-lg text-gray-700"
                      value={timeFilter}
                      onChange={(e) => setTimeFilter(e.target.value)}
                    >
                      <option value="7">Last 7 Days</option>
                      <option value="14">Last 14 Days</option>
                      <option value="30">Last 30 Days</option>
                      <option value="90">Last 90 Days</option>
                    </select>
                  </div>
                </div>

                <div className="mt-3 flex space-x-2">
                  <button
                    onClick={() => setViewMode("users")}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      viewMode === "users"
                        ? "bg-blue-50 text-blue-700 border border-blue-100"
                        : "text-gray-700 bg-white/70 border border-gray-200/80 hover:bg-blue-50/40"
                    }`}
                  >
                    All Users
                  </button>
                  <button
                    onClick={() => setViewMode("ids")}
                    className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                      viewMode === "ids"
                        ? "bg-blue-50 text-blue-700 border border-blue-100"
                        : "text-gray-700 bg-white/70 border border-gray-200/80 hover:bg-blue-50/40"
                    }`}
                  >
                    Conversation ID
                  </button>
                </div>
              </div>

              {isLoading ? (
                <div className="text-center py-12">
                  <svg
                    className="animate-spin h-8 w-8 mx-auto text-blue-500"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">
                    Loading conversations...
                  </p>
                </div>
              ) : (
                <>
                  <ul className="divide-y divide-gray-100">
                    {currentItems.length > 0 ? (
                      currentItems.map((conversation) => (
                        <li key={conversation.id}>
                          <div
                            onClick={() =>
                              handleNavigateToConversation(conversation)
                            }
                            className="block relative border-b border-gray-100 hover:bg-blue-50/40 transition duration-150 cursor-pointer"
                          >
                            <div className="flex items-center px-5 py-4 sm:px-6">
                              <div
                                className={`w-1 h-16 rounded-full mr-4 ${
                                  conversation.status === "active"
                                    ? "bg-green-500"
                                    : "bg-gray-300"
                                }`}
                              ></div>
                              <div className="min-w-0 flex-1">
                                <div className="flex justify-between gap-3">
                                  <p className="text-sm font-medium text-primary-blue truncate">
                                    {conversation.userName}
                                  </p>
                                  <p className="text-sm text-gray-500 whitespace-nowrap">
                                    ID: {conversation.id}
                                  </p>
                                </div>
                                <p className="mt-2 text-sm text-gray-600 line-clamp-1">
                                  {conversation.summary}
                                </p>
                                <div className="mt-1 flex justify-between gap-3">
                                  <div className="flex min-w-0">
                                    <p className="text-xs text-gray-500 truncate">
                                      {conversation.department}
                                    </p>
                                    <span className="mx-1 text-gray-400">•</span>
                                    <p className="text-xs text-gray-500 truncate">
                                      {conversation.topic}
                                    </p>
                                  </div>
                                  <p className="text-xs text-gray-500 whitespace-nowrap">
                                    Last updated{" "}
                                    {formatDate(conversation.lastUpdated)}
                                  </p>
                                </div>
                              </div>
                              <div>
                                <svg
                                  className="h-5 w-5 text-gray-400"
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 20 20"
                                  fill="currentColor"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))
                    ) : (
                      <li className="py-12">
                        <div className="text-center">
                          <p className="text-sm text-gray-500">
                            No conversations found matching your filters.
                          </p>
                        </div>
                      </li>
                    )}
                  </ul>

                  {/* Pagination */}
                  {filteredConversations.length > 0 && (
                    <div className="bg-white/60 backdrop-blur px-5 py-3 flex items-center justify-between border-t border-gray-100 sm:px-6">
                      <div className="flex-1 flex justify-between sm:hidden">
                        <button
                          onClick={() =>
                            setCurrentPage(Math.max(1, currentPage - 1))
                          }
                          disabled={currentPage === 1}
                          className={`relative inline-flex items-center px-4 py-2 border border-gray-200/80 text-sm font-medium rounded-lg text-gray-700 bg-white/70 hover:bg-blue-50/40 ${
                            currentPage === 1
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          Previous
                        </button>
                        <button
                          onClick={() =>
                            setCurrentPage(
                              Math.min(totalPages, currentPage + 1)
                            )
                          }
                          disabled={currentPage === totalPages}
                          className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-200/80 text-sm font-medium rounded-lg text-gray-700 bg-white/70 hover:bg-blue-50/40 ${
                            currentPage === totalPages
                              ? "opacity-50 cursor-not-allowed"
                              : ""
                          }`}
                        >
                          Next
                        </button>
                      </div>
                      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                        <div>
                          <p className="text-sm text-gray-700">
                            Showing{" "}
                            <span className="font-medium">
                              {indexOfFirstItem + 1}
                            </span>{" "}
                            to{" "}
                            <span className="font-medium">
                              {Math.min(
                                indexOfLastItem,
                                filteredConversations.length
                              )}
                            </span>{" "}
                            of{" "}
                            <span className="font-medium">
                              {filteredConversations.length}
                            </span>{" "}
                            conversations
                          </p>
                        </div>
                        <div>
                          <nav
                            className="relative z-0 inline-flex rounded-lg -space-x-px"
                            aria-label="Pagination"
                          >
                            <button
                              onClick={() =>
                                setCurrentPage(Math.max(1, currentPage - 1))
                              }
                              disabled={currentPage === 1}
                              className={`relative inline-flex items-center px-2 py-2 rounded-l-lg border border-gray-200/80 bg-white/70 text-sm font-medium text-gray-500 hover:bg-blue-50/40 ${
                                currentPage === 1
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              <span className="sr-only">Previous</span>
                              <svg
                                className="h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>

                            {/* Pages */}
                            {[...Array(totalPages).keys()].map((number) => (
                              <button
                                key={number + 1}
                                onClick={() => setCurrentPage(number + 1)}
                                className={`relative inline-flex items-center px-4 py-2 border border-gray-200/80 text-sm font-medium ${
                                  currentPage === number + 1
                                    ? "z-10 bg-blue-50 border-blue-200 text-primary-blue"
                                    : "bg-white/70 text-gray-500 hover:bg-blue-50/40"
                                }`}
                              >
                                {number + 1}
                              </button>
                            ))}

                            <button
                              onClick={() =>
                                setCurrentPage(
                                  Math.min(totalPages, currentPage + 1)
                                )
                              }
                              disabled={currentPage === totalPages}
                              className={`relative inline-flex items-center px-2 py-2 rounded-r-lg border border-gray-200/80 bg-white/70 text-sm font-medium text-gray-500 hover:bg-blue-50/40 ${
                                currentPage === totalPages
                                  ? "opacity-50 cursor-not-allowed"
                                  : ""
                              }`}
                            >
                              <span className="sr-only">Next</span>
                              <svg
                                className="h-5 w-5"
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 20 20"
                                fill="currentColor"
                                aria-hidden="true"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            </button>
                          </nav>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

ChatTrackerHome.propTypes = {
  fetchChatTrackerUsers: PropTypes.func.isRequired,
  chatTrackerData: PropTypes.object,
  loading: PropTypes.bool,
};

const mapStateToProps = (state) => ({
  chatTrackerData: state.adminUser.chatTrackerData,
  loading: state.adminUser.loading,
});

const mapDispatchToProps = {
  fetchChatTrackerUsers: FetchChatTrackerUsersAction,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatTrackerHome);
